import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Envelope, toApiError } from '@/lib/apiClient';
import type { LoginResponse, ShopSessionResponse } from '@/types/api';
import { useAuthStore } from '@/stores/authStore';
import { useLockStore } from '@/stores/lockStore';

// Step 1 of the cassa two-step login. Authenticates the shop itself (one
// credential per terminal) and returns the staff list the cashier picks from
// in step 2. The backend also sets a short-lived `shop_session` cookie.
export interface ShopLoginInput {
  login: string;
  password: string;
}

export function useShopLogin() {
  const setShopSession = useAuthStore((s) => s.setShopSession);

  return useMutation({
    mutationFn: async (input: ShopLoginInput) => {
      const res = await api.post<Envelope<ShopSessionResponse>>('/api/v1/shop/login', input);
      return res.data.data;
    },
    onSuccess: (data) => {
      setShopSession(data);
    },
    onError: (err) => toApiError(err),
  });
}

// Step 2 — the cashier picks themselves from the staff list and enters a
// 4-digit PIN. On success the backend clears `shop_session` and sets
// access_token + refresh_token cookies, so from here the app behaves like the
// old single-step session.
export interface PinLoginInput {
  userId: number;
  pin: string;
}

export function usePinLogin() {
  const setUser = useAuthStore((s) => s.setUser);
  const clearShopSession = useAuthStore((s) => s.clearShopSession);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: PinLoginInput) => {
      const res = await api.post<Envelope<LoginResponse>>('/api/v1/auth/pin', input);
      return res.data.data;
    },
    onSuccess: (data) => {
      setUser({
        id: data.userId,
        name: data.name,
        login: data.login,
        role: data.role,
        partnerId: data.partnerId,
        shopId: data.shopId,
      });
      clearShopSession();
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (err) => toApiError(err),
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post('/api/v1/auth/logout');
    },
    onSettled: () => {
      logout();
      useLockStore.getState().unenroll();
      qc.clear();
    },
  });
}
